import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { TextField } from '@mui/material';

import { getPblVettingApplications } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { Search } from '@/components/Icon';
import { PblVettingStatusBadge } from '@/components/Pbl';
import Loading from '@/components/UI/Loading';
import { H4 } from '@/components/UI/Typography/Heading';
import { Flex } from '@/layout';
import PATH from '@/router/path';
import useResize from '@/hooks/useResize';
import { theme } from '@/theme/Theme';
import { errorHandler } from '@/utils/toastHandler';

import {
  buildPblVettingApplicationParams,
  emptyPblVettingPage,
  excludeHiddenPblVettingApplications,
  filterPblVettingApplicationsByKeyword,
  getPblVettingPaginationRange,
  getPblVettingProfileCreatedAt,
  getPblVettingProfileStatus,
  getPblVettingVisiblePages,
  type PblVettingFilterStatus,
} from './vettingDashboardState';
import { useTranslation } from 'react-i18next';

const PageShell = styled.div`
  width: min(100%, 1128px);
  min-width: 0;
`;

const TitleBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  min-width: 0;
  flex-direction: ${(p) => (p.isMobile ? 'column' : 'row')};
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
  font-weight: 400;

  strong {
    font-weight: 700;
  }

  @media (max-width: 639px) {
    margin-bottom: 16px;

    > span {
      width: 100%;
      min-width: 0;
      text-align: center;
      overflow-wrap: anywhere;
    }
  }
`;

const FilterRow = styled(Flex)`
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  margin-bottom: 14px;

  .MuiFormControl-root {
    min-width: 0;
  }

  @media (max-width: 639px) {
    gap: 8px;

    .MuiFormControl-root {
      flex: 1 1 calc(100% - 50px);
      width: auto;
    }
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  min-width: 68px;
  height: 38px;
  padding: 0 22px;
  border-radius: ${(p) => p.theme.radii.base};
  color: ${(p) => p.theme.colors.light};
  background: ${(p) => (p.active ? p.theme.colors.secondary500 : p.theme.colors.gray200)};
  font-size: ${(p) => p.theme.fontSizes.base};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${(p) => (p.active ? p.theme.colors.secondary500 : p.theme.colors.gray300)};
  }

  @media (max-width: 639px) {
    min-width: 0;
    height: 40px;
    padding: 0 16px;
    font-size: 16px;
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.secondary400};
  cursor: pointer;

  img {
    width: 22px;
    height: 22px;
    filter: brightness(0) invert(1);
  }
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 34px 48px 22px;
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.light};

  @media (max-width: 768px) {
    padding: 24px 16px 18px;
  }

  @media (max-width: 639px) {
    padding: 0;
    border-radius: 0;
    background: transparent;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;

  @media (max-width: 639px) {
    overflow: visible;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    height: 56px;
    padding: 0 16px;
    border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    text-align: left;
    vertical-align: middle;
  }

  th {
    height: 30px;
    color: ${(p) => p.theme.colors.gray300};
    font-size: ${(p) => p.theme.fontSizes.base};
    font-weight: 400;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: ${(p) => p.theme.colors.gray000};
  }

  th:last-child,
  td:last-child {
    width: 170px;
  }

  @media (max-width: 639px) {
    display: block;
    min-width: 0;

    thead {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    tbody {
      display: grid;
      gap: 12px;
    }

    tbody tr {
      display: block;
      padding: 18px 12px;
      border-radius: 12px;
      background: ${(p) => p.theme.colors.light};
    }

    tbody tr:nth-child(odd) {
      background: ${(p) => p.theme.colors.gray000};
    }

    th,
    td,
    th:last-child,
    td:last-child {
      width: auto;
      height: auto;
      padding: 4px 0;
      border: 0;
      font-size: 14px;
      line-height: 1.3;
    }

    td {
      display: grid;
      grid-template-columns: 108px minmax(0, 1fr);
      gap: 12px;
      overflow-wrap: anywhere;
    }

    td::before {
      content: attr(data-label);
      color: ${(p) => p.theme.colors.gray300};
    }

    td[colspan] {
      display: block;
      padding: 18px 0;
      color: ${(p) => p.theme.colors.gray300};
      text-align: center;
    }

    td[colspan]::before {
      content: none;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  color: ${(p) => p.theme.colors.gray300};
  font-size: ${(p) => p.theme.fontSizes.sm};

  .pages {
    display: flex;
    align-items: center;
    gap: 12px;
    color: ${(p) => p.theme.colors.gray500};
  }

  select {
    width: 66px;
    height: 30px;
    border: 1px solid ${(p) => p.theme.colors.primary100};
    border-radius: ${(p) => p.theme.radii.base};
    background: ${(p) => p.theme.colors.light};
    color: ${(p) => p.theme.colors.gray500};
    text-align: center;
  }

  button {
    min-width: 32px;
    height: 32px;
    border-radius: ${(p) => p.theme.radii.base};
    background: ${(p) => p.theme.colors.gray100};
    color: ${(p) => p.theme.colors.gray500};
    cursor: pointer;
  }

  button.active {
    background: ${(p) => p.theme.colors.primary300};
    color: ${(p) => p.theme.colors.light};
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  @media (max-width: 768px) {
    align-items: center;
    flex-direction: column;
    text-align: center;

    .pages {
      flex-wrap: wrap;
      justify-content: center;
      gap: 8px;
    }
  }
`;

const StateMessage = styled.div`
  padding: 36px 16px;
  color: ${(p) => p.theme.colors.gray400};
  text-align: center;
`;

const vettingFilterStatuses = new Set<PblVettingFilterStatus>([
  'all',
  'pending_review',
  'approved',
  'resubmit_required',
  'rejected',
  'inactive',
  'withdrawn',
]);

const getVettingFilterStatus = (searchParams: URLSearchParams): PblVettingFilterStatus => {
  const status = searchParams.get('status') as PblVettingFilterStatus | null;
  return status && vettingFilterStatuses.has(status) ? status : 'all';
};

const PblVettingDashboard = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useResize();
  const requestedStatus = getVettingFilterStatus(searchParams);
  const [activeStatus, setActiveStatus] = useState<PblVettingFilterStatus>(requestedStatus);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [applicationsPage, setApplicationsPage] = useState(emptyPblVettingPage());
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const filterTabs = useMemo<Array<{ label: string; status: PblVettingFilterStatus }>>(
    () => [
      { label: t('pbl.pagesHardcoded.vettingDashboard.all'), status: 'all' },
      { label: t('pbl.dashboardCalendarNotifications.dashboard.caseTable.statuses.pending'), status: 'pending_review' },
      { label: t('pbl.pagesHardcoded.vettingDashboard.approved'), status: 'approved' },
      { label: t('pbl.pagesHardcoded.vettingDashboard.underReSubmission'), status: 'resubmit_required' },
      { label: t('pbl.pagesHardcoded.vettingDashboard.rejected'), status: 'rejected' },
      { label: t('pbl.pagesHardcoded.vettingDashboard.inactive'), status: 'inactive' },
      { label: t('pbl.pagesHardcoded.vettingDashboard.withdrawn'), status: 'withdrawn' },
    ],
    [t]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchApplications = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await getPblVettingApplications(
          buildPblVettingApplicationParams({ status: activeStatus, page, perPage })
        );

        if (!isMounted) return;

        if (!response.data.success) {
          const message = response.data.message || 'Failed to load attorney applications.';
          setApplicationsPage(emptyPblVettingPage(page, perPage));
          setErrorMessage(message);
          errorHandler(message);
          return;
        }

        setApplicationsPage(response.data.data || emptyPblVettingPage(page, perPage));
      } catch (error: any) {
        if (!isMounted) return;

        const message =
          error.response?.data?.message ||
          error.response?.data?.data ||
          error.message ||
          'Failed to load attorney applications.';
        setApplicationsPage(emptyPblVettingPage(page, perPage));
        setErrorMessage(message);
        errorHandler(message);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchApplications();

    return () => {
      isMounted = false;
    };
  }, [activeStatus, page, perPage]);

  const visibleApplications = useMemo(
    () => filterPblVettingApplicationsByKeyword(excludeHiddenPblVettingApplications(applicationsPage.data), keyword),
    [applicationsPage.data, keyword]
  );
  const paginationRange = getPblVettingPaginationRange(applicationsPage);
  const visiblePages = getPblVettingVisiblePages(applicationsPage);
  const lastPage = Math.max(1, applicationsPage.last_page || 1);
  const hasKeyword = keyword.trim().length > 0;
  const footerFrom = hasKeyword ? (visibleApplications.length > 0 ? 1 : 0) : paginationRange.from;
  const footerTo = hasKeyword ? visibleApplications.length : paginationRange.to;
  const footerTotal = hasKeyword ? visibleApplications.length : applicationsPage.total;

  const goDetail = (applicationId: number) => {
    navigate(PATH.pbl.vettingDetail.replace(':profileId', String(applicationId)));
  };

  const updateStatus = (status: PblVettingFilterStatus) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (status === 'all') nextSearchParams.delete('status');
    else nextSearchParams.set('status', status);

    setActiveStatus(status);
    setPage(1);
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    setActiveStatus(requestedStatus);
    setPage(1);
  }, [requestedStatus]);

  const updatePerPage = (value: number) => {
    setPerPage(value);
    setPage(1);
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <span>
            <strong>{t('pbl.pagesHardcoded.vettingDashboard.reviewOfProbonoLawyersApplication')}</strong>
          </span>
        </TitleBar>
      }
    >
      <PageShell>
        <FilterRow alignItems="center" gridGap={10}>
          {filterTabs.map((tab) => (
            <FilterButton
              key={tab.status}
              active={activeStatus === tab.status}
              onClick={() => updateStatus(tab.status)}
              type="button"
            >
              <H4 color={theme.colors.light}>
                {tab.label}
              </H4>
            </FilterButton>
          ))}
          <TextField
            size="small"
            value={keyword}
            placeholder={t('pbl.pagesHardcoded.vettingDashboard.search')}
            onChange={(event) => setKeyword(event.target.value)}
            sx={{ width: isMobile ? '100%' : 286, background: '#fff' }}
          />
          <SearchButton type="button" aria-label={t('pbl.pagesHardcoded.vettingDashboard.search')}>
            <img src={Search.SearchSVG_B} alt="" />
          </SearchButton>
        </FilterRow>

        <Card>
          {isLoading ? (
            <Loading />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>{t('pbl.pagesHardcoded.vettingDashboard.lawyerName')}</th>
                    <th>{t('pbl.pagesHardcoded.vettingDashboard.creationDate')}</th>
                    <th>{t('pbl.pagesHardcoded.vettingDashboard.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleApplications.map((application) => (
                    <tr key={application.id} onClick={() => goDetail(application.id)}>
                      <td data-label={t('pbl.pagesHardcoded.vettingDashboard.lawyerName')}>
                        {application.full_name_en} / {application.full_name_zh || '-'}
                      </td>
                      <td data-label={t('pbl.pagesHardcoded.vettingDashboard.creationDate')}>
                        {getPblVettingProfileCreatedAt(application)}
                      </td>
                      <td data-label={t('pbl.pagesHardcoded.vettingDashboard.status')}>
                        <PblVettingStatusBadge status={getPblVettingProfileStatus(application)} />
                      </td>
                    </tr>
                  ))}
                  {visibleApplications.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <StateMessage>{errorMessage || t('pbl.pagesHardcoded.vettingDashboard.noAttorneyApplicationsFound')}</StateMessage>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}
          <Footer>
            <span>
              {t('pbl.pagesHardcoded.vettingDashboard.showing')}{footerFrom} to {footerTo} of {footerTotal} entries
            </span>
            <div className="pages">
              <span>{t('pbl.pagesHardcoded.vettingDashboard.display')}</span>
              <select
                value={perPage}
                onChange={(event) => updatePerPage(Number(event.target.value))}
                aria-label={t('pbl.pagesHardcoded.vettingDashboard.displayCount')}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              <button type="button" disabled={page <= 1 || isLoading} onClick={() => setPage((value) => value - 1)}>
                {'<'}
              </button>
              {visiblePages[0] > 1 && (
                <>
                  <button type="button" onClick={() => setPage(1)} disabled={isLoading}>
                    1
                  </button>
                  {visiblePages[0] > 2 && <span>...</span>}
                </>
              )}
              {visiblePages.map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={pageNumber === page ? 'active' : undefined}
                  disabled={isLoading}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              {visiblePages[visiblePages.length - 1] < lastPage && (
                <>
                  {visiblePages[visiblePages.length - 1] < lastPage - 1 && <span>...</span>}
                  <button type="button" onClick={() => setPage(lastPage)} disabled={isLoading}>
                    {lastPage}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={page >= lastPage || isLoading}
                onClick={() => setPage((value) => value + 1)}
              >
                {'>'}
              </button>
            </div>
          </Footer>
        </Card>
      </PageShell>
    </PageContainer>
  );
};

export default PblVettingDashboard;
